import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Send, X, MessageCircle, ThumbsUp, ThumbsDown,
  ShieldCheck, Star, Mail, Download, Copy, Check,
} from "lucide-react";

/**
 * Diplomexa Smart Support — embedded chat panel.
 *
 * Floating launcher button bottom-right. Opens a 380px-wide drawer with the
 * conversation transcript. Never mentions "AI" / "chatbot" / "Claude" — the
 * brand is "Diplomexa Smart Support" / "Ask Diplomexa".
 */
export default function SmartSupportPanel() {
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showCsat, setShowCsat] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcriptSent, setTranscriptSent] = useState(false);
  const listRef = useRef(null);

  // Start a fresh conversation when the panel opens for the first time
  useEffect(() => {
    if (!open || conv) return;
    (async () => {
      try {
        const { data } = await api.post("/smart-support/conversations", {});
        setConv(data.conversation);
        setMessages([data.greeting]);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not start Smart Support");
      }
    })();
  }, [open, conv]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length, sending]);

  const send = async () => {
    if (!input.trim() || !conv || sending) return;
    const msg = input.trim();
    setInput("");
    setSending(true);
    const optimisticUser = {
      id: `tmp-${Date.now()}`,
      role: "user",
      text: msg,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticUser]);
    try {
      const { data } = await api.post("/smart-support/message", {
        conversation_id: conv.id,
        message: msg,
      });
      setMessages((prev) => {
        const withoutTmp = prev.filter((m) => m.id !== optimisticUser.id);
        return [...withoutTmp, data.user_message, data.assistant_message];
      });
      if (data.escalated) {
        toast.success(`Escalated to human support. Ticket ${data.ticket?.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setSending(false);
    }
  };

  const rateMessage = async (messageId, rating) => {
    try {
      await api.post("/smart-support/feedback", {
        conversation_id: conv.id,
        message_id: messageId,
        rating,
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, _rated: rating } : m))
      );
    } catch {
      // silent
    }
  };

  const closeConversation = async (csat) => {
    if (!conv) return;
    try {
      await api.post("/smart-support/conversations/close", {
        conversation_id: conv.id,
        csat,
      });
    } catch {
      // ignore
    }
    setShowCsat(false);
    setShowTranscript(false);
    setTranscriptSent(false);
    setConv(null);
    setMessages([]);
    setOpen(false);
  };

  const requestTranscript = async (delivery, confirm = false) => {
    if (!conv) return;
    try {
      const { data } = await api.post("/smart-support/transcript", {
        conversation_id: conv.id,
        delivery,
        confirm,
      });
      if (delivery === "copy" && data.text) {
        try {
          await navigator.clipboard.writeText(data.text);
          toast.success("Transcript copied to clipboard");
        } catch {
          toast.error("Could not copy — try Download instead");
        }
      } else if (delivery === "download" && data.text) {
        const blob = new Blob([data.text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `diplomexa-smart-support-${conv.id}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Transcript downloaded");
      } else if (delivery === "email") {
        if (data.delivery === "email_mock") {
          toast.warning(data.message);
        } else {
          toast.success(`Sent to ${data.delivered_to}`);
        }
        setTranscriptSent(true);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not deliver transcript");
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-5 z-50 h-12 w-12 rounded-full bg-gradient-to-br from-[#0A192F] to-[#0F4C81] border border-[#D4AF37]/60 shadow-[0_8px_30px_-4px_rgba(212,175,55,0.4)] flex items-center justify-center hover:scale-105 transition-transform group"
          data-testid="smart-support-launcher"
          aria-label="Ask Diplomexa Smart Support"
        >
          <MessageCircle className="h-5 w-5 text-[#D4AF37]" />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#D4AF37] animate-pulse" />
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div
          className="fixed bottom-24 right-5 z-50 w-[min(380px,calc(100vw-2.5rem))] h-[min(580px,calc(100vh-7rem))] rounded-2xl bg-white border border-slate-200 shadow-[0_30px_80px_-20px_rgba(15,76,129,0.35)] flex flex-col overflow-hidden"
          data-testid="smart-support-panel"
        >
          <header className="px-5 py-4 bg-gradient-to-br from-[#0A192F] to-[#0F2C50] text-white flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Diplomexa
              </p>
              <p className="mt-1 text-base font-semibold">Smart Support</p>
              <p className="text-[11px] text-slate-300 mt-0.5 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3 text-[#D4AF37]" />
                Concierge for your conference journey
              </p>
            </div>
            <button
              type="button"
              onClick={() => (messages.length > 1 ? setShowCsat(true) : setOpen(false))}
              className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center"
              data-testid="smart-support-close"
              aria-label="Close Smart Support"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          {/* Messages */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50"
            data-testid="smart-support-messages"
          >
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                m={m}
                onRate={(rating) => rateMessage(m.id, rating)}
              />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-slate-500 px-2">
                <span className="inline-block h-2 w-2 rounded-full bg-[#0F4C81] animate-pulse" />
                Smart Support is thinking…
              </div>
            )}
          </div>

          {showCsat && (
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
              {!showTranscript ? (
                <>
                  <p className="text-xs font-semibold text-[#0A192F]">
                    Want a copy of this conversation?
                  </p>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setShowTranscript(true)}
                      className="text-xs px-3 py-1.5 rounded-md border border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81]/10 flex items-center gap-1.5"
                      data-testid="smart-support-transcript-toggle"
                    >
                      <Mail className="h-3 w-3" /> Get transcript
                    </button>
                    <button
                      type="button"
                      onClick={() => requestTranscript("download")}
                      className="text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100 flex items-center gap-1.5"
                      data-testid="smart-support-transcript-download"
                    >
                      <Download className="h-3 w-3" /> Download
                    </button>
                    <button
                      type="button"
                      onClick={() => requestTranscript("copy")}
                      className="text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100 flex items-center gap-1.5"
                      data-testid="smart-support-transcript-copy"
                    >
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                  <p className="mt-3 text-xs font-semibold text-[#0A192F]">
                    How was your experience?
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => closeConversation(n)}
                        className="h-8 w-8 rounded-full bg-white border border-slate-200 hover:bg-[#D4AF37]/15 hover:border-[#D4AF37] flex items-center justify-center"
                        data-testid={`smart-support-csat-${n}`}
                      >
                        <Star className="h-4 w-4 text-[#D4AF37]" />
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => closeConversation(null)}
                      className="ml-2 text-xs text-slate-500 hover:text-[#0A192F]"
                      data-testid="smart-support-csat-skip"
                    >
                      Skip
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-2" data-testid="smart-support-transcript-confirm">
                  <p className="text-xs font-semibold text-[#0A192F]">
                    Email this conversation to me?
                  </p>
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    We&apos;ll send the full transcript (including event name, ticket id if any,
                    and timestamps) to your registered email. By confirming you agree to
                    receive this transactional email.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => requestTranscript("email", true)}
                      disabled={transcriptSent}
                      className="text-xs px-3 py-1.5 rounded-md bg-[#0F4C81] hover:bg-[#0A3860] text-white flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="smart-support-transcript-email-confirm"
                    >
                      {transcriptSent ? <Check className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                      {transcriptSent ? "Sent" : "Confirm & send"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTranscript(false)}
                      className="text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="px-4 py-3 border-t border-slate-200 bg-white flex items-center gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Diplomexa anything…"
              className="flex-1 text-sm px-3 py-2 rounded-md border border-slate-200 focus:border-[#0F4C81] focus:ring-1 focus:ring-[#0F4C81] outline-none"
              data-testid="smart-support-input"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="h-9 w-9 rounded-md bg-[#0F4C81] hover:bg-[#0A3860] text-white flex items-center justify-center disabled:opacity-50"
              data-testid="smart-support-send"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function MessageBubble({ m, onRate }) {
  const isUser = m.role === "user";
  const isEsc = !!m.meta?.escalated;
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] ${isUser ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <div
          className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
            isUser
              ? "bg-[#0A192F] text-white rounded-br-md"
              : isEsc
              ? "bg-amber-50 border border-amber-300 text-[#0A192F] rounded-bl-md"
              : "bg-white border border-slate-200 text-[#0A192F] rounded-bl-md"
          }`}
          data-testid={`smart-support-msg-${m.role}`}
        >
          {m.text}
          {isEsc && m.meta?.ticket_id && (
            <p className="mt-2 text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
              Ticket {m.meta.ticket_id} · Human team notified
            </p>
          )}
        </div>
        {!isUser && (
          <div className="flex items-center gap-1.5 px-1">
            <button
              type="button"
              onClick={() => onRate(1)}
              className={`h-6 w-6 rounded-full flex items-center justify-center transition-colors ${
                m._rated === 1
                  ? "bg-emerald-100 text-emerald-700"
                  : "text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"
              }`}
              aria-label="Helpful"
              data-testid={`smart-support-thumb-up-${m.id}`}
            >
              <ThumbsUp className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => onRate(-1)}
              className={`h-6 w-6 rounded-full flex items-center justify-center transition-colors ${
                m._rated === -1
                  ? "bg-rose-100 text-rose-700"
                  : "text-slate-400 hover:text-rose-600 hover:bg-rose-50"
              }`}
              aria-label="Not helpful"
              data-testid={`smart-support-thumb-down-${m.id}`}
            >
              <ThumbsDown className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
